import { ChangeEvent, FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { isNotEmpty } from '../../../../../_metronic/helpers'
import { initialPurchasedProduct, PurchasedProduct } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { PurchasedProductsListLoading } from '../components/loading/PurchasedProductsListLoading'
import { createPurchasedProduct, updatePurchasedProduct } from '../core/_requests'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { FormattedMessage, useIntl } from 'react-intl'
import axios from 'axios'
import { Service } from '../../../service/services-list/core/_models'
import { Supplier } from '../../../supplier/suppliers-list/core/_models'

type Props = {
  isPurchasedProductLoading: boolean
  purchasedproduct: PurchasedProduct
}

const editPurchasedProductSchema = Yup.object().shape({
  product_name: Yup.string().required('Product name is required'),
  quantity: Yup.number().required('Quantity is required').min(1, 'Quantity must be at least 1'),
  purchase_price: Yup.number().required('Purchase price is required').min(0, 'Price must be positive'),
  purchase_date: Yup.date().required('Purchase date is required'),
  supplier_id: Yup.string().required('Supplier is required'),
  service_id: Yup.string().required('Service is required'),
  status: Yup.boolean().required('Status is required'),
})

const PurchasedProductEditModalForm: FC<Props> = ({ purchasedproduct, isPurchasedProductLoading }) => {
  const API_URL = process.env.REACT_APP_THEME_API_URL
  const SUPPLIER_URL = `${API_URL}/admin/suppliers`
  const SERVICE_URL = `${API_URL}/admin/services`
  const intl = useIntl()

  const [suppliers, setSuppliers] = useState<Supplier[]>()
  const [services, setServices] = useState<Service[]>()

  useEffect(() => {
    axios.get(SUPPLIER_URL).then((response) => {
      const data = response.data
      if (data.success) {
        setSuppliers(data.data.suppliers)
      }
    }).catch((error) => console.error(error))

    axios.get(SERVICE_URL).then((response) => {
      const data = response.data
      if (data.success) {
        setServices(data.data.services)
      }
    }).catch((error) => console.error(error))
  }, [])

  const { setItemIdForUpdate } = useListView()
  const { refetch } = useQueryResponse()

  const [purchasedproductForEdit] = useState<PurchasedProduct>({
    ...purchasedproduct,
    product_name: purchasedproduct.product_name || initialPurchasedProduct.product_name,
    supplier_id: purchasedproduct.supplier_id || initialPurchasedProduct.supplier_id,
    service_id: purchasedproduct.service_id || initialPurchasedProduct.service_id,
    quantity: purchasedproduct.quantity || initialPurchasedProduct.quantity,
    purchase_price: purchasedproduct.purchase_price || initialPurchasedProduct.purchase_price,
    purchase_date: purchasedproduct.purchase_date || initialPurchasedProduct.purchase_date,
    status: purchasedproduct.status ?? initialPurchasedProduct.status,
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: purchasedproductForEdit,
    validationSchema: editPurchasedProductSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (isNotEmpty(values.id)) {
          await updatePurchasedProduct(values)
        } else {
          await createPurchasedProduct(values)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  return (
    <>
      <form id='kt_modal_add_purchasedproduct_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div className='d-flex flex-column scroll-y me-n7 pe-7' id='kt_modal_add_purchasedproduct_scroll' data-kt-scroll='true' data-kt-scroll-activate='{default: false, lg: true}' data-kt-scroll-max-height='auto' data-kt-scroll-dependencies='#kt_modal_add_purchasedproduct_header' data-kt-scroll-wrappers='#kt_modal_add_purchasedproduct_scroll' data-kt-scroll-offset='300px'>
          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='form-label required'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.SUPPLIER' defaultMessage='Supplier' /></label>
              <select {...formik.getFieldProps('supplier_id')} className={clsx('form-select form-control', { 'is-invalid': formik.touched.supplier_id && formik.errors.supplier_id })} disabled={formik.isSubmitting || isPurchasedProductLoading}>
                <option value=''>{intl.formatMessage({ id: 'FORM.GENERAL.SELECT', defaultMessage: 'Select' })}</option>
                {suppliers && suppliers.map(supplier => <option key={supplier.id} value={supplier.id}>{supplier.supplier_name}</option>)}
              </select>
              {formik.touched.supplier_id && formik.errors.supplier_id && <div className='fv-plugins-message-container'><span role='alert'>{formik.errors.supplier_id}</span></div>}
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='form-label required'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.SERVICE' defaultMessage='Service' /></label>
              <select {...formik.getFieldProps('service_id')} className={clsx('form-select form-control', { 'is-invalid': formik.touched.service_id && formik.errors.service_id })} disabled={formik.isSubmitting || isPurchasedProductLoading}>
                <option value=''>{intl.formatMessage({ id: 'FORM.GENERAL.SELECT', defaultMessage: 'Select' })}</option>
                {services && services.map(service => <option key={service.id} value={service.id}>{service.service_category?.category_name+"("+service.company?.company_name+")"}</option>)}
              </select>
              {formik.touched.service_id && formik.errors.service_id && <div className='fv-plugins-message-container'><span role='alert'>{""+formik.errors.service_id}</span></div>}
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.PRODUCTNAME' defaultMessage='Product Name' /></label>
              <input {...formik.getFieldProps('product_name')} className={clsx('form-control', { 'is-invalid': formik.touched.product_name && formik.errors.product_name })} disabled={formik.isSubmitting || isPurchasedProductLoading} />
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.QUANTITY' defaultMessage='Quantity' /></label>
              <input type='number' {...formik.getFieldProps('quantity')} className={clsx('form-control', { 'is-invalid': formik.touched.quantity && formik.errors.quantity })} disabled={formik.isSubmitting || isPurchasedProductLoading} />
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.PURCHASEPRICE' defaultMessage='Purchase Price' /></label>
              <input type='number' {...formik.getFieldProps('purchase_price')} className={clsx('form-control', { 'is-invalid': formik.touched.purchase_price && formik.errors.purchase_price })} disabled={formik.isSubmitting || isPurchasedProductLoading} />
            </div>

            <div className='col-lg-6 fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.PURCHASEDATE' defaultMessage='Purchase Date' /></label>
              <input type='date' {...formik.getFieldProps('purchase_date')} className={clsx('form-control', { 'is-invalid': formik.touched.purchase_date && formik.errors.purchase_date })} disabled={formik.isSubmitting || isPurchasedProductLoading} />
            </div>
          </div>

          <div className='row'>
            <div className='col-lg-6 fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'><FormattedMessage id='PURCHASEDPRODUCT.FORM.INPUT.STATUS' defaultMessage='Status' /></label>
              <select {...formik.getFieldProps('status')} className='form-select form-control' disabled={formik.isSubmitting || isPurchasedProductLoading}>
                <option value='1'>{intl.formatMessage({ id: 'FORM.GENERAL.ACTIVE', defaultMessage: 'Active' })}</option>
                <option value='0'>{intl.formatMessage({ id: 'FORM.GENERAL.INACTIVE', defaultMessage: 'Inactive' })}</option>
              </select>
            </div>
          </div>
        </div>

        <div className='text-center pt-15'>
          <button type='reset' onClick={() => cancel()} className='btn btn-light me-3' disabled={formik.isSubmitting || isPurchasedProductLoading}><FormattedMessage id='FORM.GENERAL.DISCARD' /></button>
          <button type='submit' className='btn btn-primary' disabled={isPurchasedProductLoading || formik.isSubmitting || !formik.isValid || !formik.touched}>
            <span className='indicator-label'><FormattedMessage id='FORM.GENERAL.SUBMIT' defaultMessage='Submit' /></span>
            {(formik.isSubmitting || isPurchasedProductLoading) && <span className='indicator-progress'><FormattedMessage id='FORM.GENERAL.PLEASEWAIT' />{' '}<span className='spinner-border spinner-border-sm align-middle ms-2'></span></span>}
          </button>
        </div>
      </form>
      {(formik.isSubmitting || isPurchasedProductLoading) && <PurchasedProductsListLoading />}
    </>
  )
}

export { PurchasedProductEditModalForm }
